import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowUpRight } from "lucide-react";
import { fetchProducts, absoluteUrl } from "@/lib/api";

const MAX = 4;

export default function FeaturedProducts() {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts()
      .then((all) => setFeatured(all.filter((p) => p.featured).slice(0, MAX)))
      .catch(() => setFeatured([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || featured.length === 0) return null;

  const [hero, ...rest] = featured;

  return (
    <section data-testid="featured-section" className="py-20 md:py-24 bg-gradient-to-b from-white via-emerald-50/40 to-white relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute top-10 left-1/3 w-72 h-72 rounded-full bg-emerald-300/30 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-between gap-4 mb-10"
        >
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-700 text-xs font-semibold tracking-wider uppercase">
              <Sparkles size={12} /> Featured Today
            </span>
            <span className="text-sm text-slate-500 hidden sm:inline">Top categories moving this season</span>
          </div>
          <a href="#products" data-testid="featured-see-all" className="text-sm text-blue-600 hover:text-blue-700 font-medium hidden md:inline-flex items-center gap-1 group">
            See full catalog
            <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Hero featured */}
          <motion.article
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            data-testid={`featured-hero-${hero.id}`}
            className={`relative overflow-hidden rounded-3xl bg-slate-950 text-white group ${rest.length > 0 ? "lg:col-span-7" : "lg:col-span-12"} aspect-[16/10] lg:aspect-auto lg:min-h-[420px]`}
          >
            <img src={absoluteUrl(hero.image_url)} alt={hero.name} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-tr from-slate-950 via-slate-950/60 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8 md:p-10">
              <span className="inline-flex w-fit items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-emerald-300 text-[10px] font-bold uppercase tracking-widest mb-4">
                <Sparkles size={10} /> Spotlight
              </span>
              <p className="text-xs uppercase tracking-widest text-emerald-300/90 mb-2">{hero.category}</p>
              <h3 className="font-heading text-3xl md:text-4xl font-semibold leading-tight max-w-md">{hero.name}</h3>
              <p className="text-sm text-slate-300 mt-3 max-w-md leading-relaxed">{hero.description}</p>
              <a href="#contact" data-testid={`featured-cta-${hero.id}`} className="mt-6 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-full text-sm font-medium w-fit transition-colors shadow-lg shadow-emerald-500/20">
                Inquire <ArrowUpRight size={14} />
              </a>
            </div>
          </motion.article>

          {/* Secondary featured */}
          {rest.length > 0 && (
            <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-5">
              {rest.map((p, i) => (
                <motion.article
                  key={p.id}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 + i * 0.06 }}
                  data-testid={`featured-secondary-${p.id}`}
                  className="relative bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group flex"
                >
                  <div className="w-28 sm:w-32 shrink-0 bg-slate-50 relative">
                    <img src={absoluteUrl(p.image_url)} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="flex-1 p-5 min-w-0">
                    <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1.5">{p.category}</span>
                    <h4 className="font-heading text-base font-semibold text-slate-900 truncate">{p.name}</h4>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2 leading-relaxed">{p.description}</p>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
